// Pure evaluation math shared by the bot and the analysis board (plan §9, §10).
// Stockfish reports a `score` from the *side-to-move* perspective; everything the
// UI shows (eval bar, move judgements) is normalised to White's perspective and,
// for judgements, to a win-probability so that a swing near 0.00 counts far more
// than the same centipawn swing in an already-decided position — lila's approach.

/** A Stockfish score, from the perspective of the side to move. */
export interface Score {
  /** Centipawns (absent when a forced mate is seen). */
  cp?: number;
  /** Moves to mate, signed (+ = side to move mates, − = gets mated). */
  mate?: number;
}

/** Sigmoid steepness fitted by lichess (win% ≈ 50 at cp 0). */
const WIN_MULT = 0.00368208;
/** A seen mate is treated as this many centipawns for the eval bar / win%. */
const MATE_CP = 12800;

/** Flip a side-to-move score to White's perspective. */
export function whitePovScore(score: Score, turn: "white" | "black"): Score {
  if (turn === "white") return score;
  return {
    cp: score.cp === undefined ? undefined : -score.cp,
    mate: score.mate === undefined ? undefined : -score.mate,
  };
}

/** White's win probability (0–100) for a White-perspective score. */
export function winProbWhite(score: Score): number {
  if (score.mate !== undefined) {
    if (score.mate > 0) return 100;
    if (score.mate < 0) return 0;
    return 50; // shouldn't occur for a searchable position
  }
  const cp = clamp(score.cp ?? 0, -MATE_CP, MATE_CP);
  return 100 / (1 + Math.exp(-WIN_MULT * cp));
}

/** Win probability (0–100) from a given colour's perspective. */
export function povWinProb(whiteScore: Score, color: "white" | "black"): number {
  const w = winProbWhite(whiteScore);
  return color === "white" ? w : 100 - w;
}

/** Human eval string from White's perspective: `#3`, `#-2`, `+1.3`, `-0.5`. */
export function formatEval(score: Score): string {
  if (score.mate !== undefined) return `#${score.mate > 0 ? "" : "-"}${Math.abs(score.mate)}`;
  const pawns = (score.cp ?? 0) / 100;
  const sign = pawns > 0 ? "+" : pawns < 0 ? "−" : "";
  return `${sign}${Math.abs(pawns).toFixed(1)}`;
}

export type Judgement = "blunder" | "mistake" | "inaccuracy";

/** Win-probability drop (percentage points) that trips each judgement. */
const THRESHOLDS: [Judgement, number][] = [
  ["blunder", 30],
  ["mistake", 20],
  ["inaccuracy", 10],
];

/**
 * Judge a move by how much win probability the mover gave up.
 * `before`/`after` are the mover's win% in the positions immediately before and
 * after the move. Returns `null` for a fine move.
 */
export function judgeMove(beforePovWin: number, afterPovWin: number): Judgement | null {
  const drop = beforePovWin - afterPovWin;
  for (const [name, t] of THRESHOLDS) if (drop >= t) return name;
  return null;
}

/** NAG-ish glyph for a judgement (shown next to the move in the list). */
export function judgementGlyph(j: Judgement): string {
  return j === "blunder" ? "??" : j === "mistake" ? "?" : "?!";
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
